/***
 *
 *   BRAND
 *   Derives a full token palette from one tenant hex, for the white-labelled
 *   public project pages.
 *
 *   A tenant gives us `#0f5132`. The app wants `--primary-50` through `-900`,
 *   an accent ramp, a foreground and a focus ring — in both themes, and as
 *   space-separated HSL triplets, because tailwind.config.js wraps every colour
 *   as `hsl(var(--x) / <alpha-value>)`.
 *
 *   Two rules make this safe rather than merely colourful:
 *
 *   1. Stop 600 IS the colour the tenant picked. `text-primary-600` (View
 *      tickets) and `bg-accent-500` (Continue / Submit, shifted onto 600)
 *      therefore render the hex as-is, not a darkened cousin. The other stops
 *      follow the STOCK indigo ramp's S/L deltas (lifted from css/tokens.css)
 *      relative to that anchor, so hover and tinted surfaces still have a ramp.
 *
 *   2. Stops 800-900 are clamped until white text on them clears WCAG AA. The
 *      project-auth panel is `from-primary-800 to-primary-900` with `text-white`,
 *      and a tenant who picks #ffd400 would otherwise break it.
 *      utilities/brand.test.js is what enforces this — do not relax the floor
 *      without deleting a passing test.
 *
 *   Pure on purpose: no React import, so it is testable in isolation.
 *
 **********/

// The stock indigo ramp, verbatim from css/tokens.css:45-54.
const STOCK_S = [100, 100, 100, 100, 95, 84, 75, 58, 47, 47];
const STOCK_L = [97, 94, 88, 81, 75, 67, 59, 51, 42, 34];
const STOPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];

// STOCK_S at the 600 stop. Scaling the tenant's saturation against this is what
// keeps a muted brand muted and a vivid one vivid across the whole ramp.
const S_REF = 75;

// The app's own dark ink (--foreground), not pure black — that is what text
// actually sits at, so it is what the foreground contrast is measured against.
const FOREGROUND_DARK = { h: 222, s: 47, l: 11 };

// WCAG AA for normal-size text. The stops below carry small white labels.
const AA_NORMAL = 4.5;

// The tenant hex lands here. Links (`text-primary-600`) and the intake accent
// button (`bg-accent-500` → this stop) read this value as-is.
const BRAND_INDEX = STOPS.indexOf(600);

// Stops that carry small white text on the project-auth gradient
// (`from-primary-800 to-primary-900`). 600 is no longer clamped — that is what
// was making the public-page links and button darker than the picked colour.
const FIRST_CLAMPED_INDEX = STOPS.indexOf(800);

// Below ~22% saturation a derived ramp reads as dead grey; above ~96% the light
// stops fluoresce. This is the one place a tenant's hex is not honoured
// literally, so it is deliberately narrow and documented.
const MIN_SATURATION = 22;
const MAX_SATURATION = 96;

// An achromatic brand has no meaningful hue — rgbToHsl reports 0deg for pure
// grey, which is red. Forcing MIN_SATURATION onto that turns #808080 into dusty
// pink. Below this threshold the ramp is built as a true neutral instead, which
// is what a tenant who picked grey actually asked for.
const NEUTRAL_SATURATION = 10;

// Floor for the clamp. A brand this dark has already lost its hue identity;
// going further just makes stops indistinguishable from each other.
const MIN_LIGHTNESS = 12;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/*
 * parseHex()
 * '#abc' or '#aabbcc' -> { r, g, b }, or null for anything else.
 */
function parseHex(input) {
    if (typeof input !== 'string') return null;

    const hex = input.trim().toLowerCase();

    if (!/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/.test(hex)) return null;

    const full =
        hex.length === 4
            ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
            : hex;

    return {
        r: parseInt(full.slice(1, 3), 16),
        g: parseInt(full.slice(3, 5), 16),
        b: parseInt(full.slice(5, 7), 16),
    };
}

/*
 * rgbToHsl() / hslToRgb()
 * Standard conversions. Hue in degrees, saturation and lightness in percent.
 */
function rgbToHsl({ r, g, b }) {
    const rn = r / 255;
    const gn = g / 255;
    const bn = b / 255;

    const max = Math.max(rn, gn, bn);
    const min = Math.min(rn, gn, bn);
    const delta = max - min;
    const l = (max + min) / 2;

    let h = 0;
    let s = 0;

    if (delta !== 0) {
        s = delta / (1 - Math.abs(2 * l - 1));

        if (max === rn) h = ((gn - bn) / delta) % 6;
        else if (max === gn) h = (bn - rn) / delta + 2;
        else h = (rn - gn) / delta + 4;

        h *= 60;
        if (h < 0) h += 360;
    }

    return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function hslToRgb({ h, s, l }) {
    const sn = s / 100;
    const ln = l / 100;

    const c = (1 - Math.abs(2 * ln - 1)) * sn;
    const hp = (((h % 360) + 360) % 360) / 60;
    const x = c * (1 - Math.abs((hp % 2) - 1));

    const [r1, g1, b1] =
        hp < 1 ? [c, x, 0]
        : hp < 2 ? [x, c, 0]
        : hp < 3 ? [0, c, x]
        : hp < 4 ? [0, x, c]
        : hp < 5 ? [x, 0, c]
        : [c, 0, x];

    const m = ln - c / 2;

    return {
        r: Math.round((r1 + m) * 255),
        g: Math.round((g1 + m) * 255),
        b: Math.round((b1 + m) * 255),
    };
}

/*
 * relativeLuminance() / contrastRatio()
 * WCAG 2.1 definitions. Used to decide the clamp and the foreground, so these
 * are the numbers the tests assert against.
 */
function relativeLuminance(hsl) {
    const { r, g, b } = hslToRgb(hsl);

    const channel = (value) => {
        const v = value / 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    };

    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

export function contrastRatio(a, b) {
    const la = relativeLuminance(a);
    const lb = relativeLuminance(b);
    const lighter = Math.max(la, lb);
    const darker = Math.min(la, lb);

    return (lighter + 0.05) / (darker + 0.05);
}

const WHITE = { h: 0, s: 0, l: 100 };

// '243 75% 59%' — the shape tailwind.config.js expects inside hsl().
const triplet = ({ h, s, l }) => `${h} ${s}% ${l}%`;

/*
 * deriveBrandRamp()
 * hex -> { 50..900, foreground } as HSL triplet strings, or null.
 *
 * Returning null for unusable input is what lets every caller fall back to the
 * stock Valetify tokens by doing nothing at all.
 */
export function deriveBrandRamp(hex) {
    const rgb = parseHex(hex);
    if (!rgb) return null;

    const base = rgbToHsl(rgb);

    // Hue is kept exactly as given. The stock ramp drifts 226deg -> 245deg
    // across its stops, but that is an indigo-specific choice; reproducing it
    // would rotate a tenant's brand away from the colour they asked for.
    const neutral = base.s < NEUTRAL_SATURATION;
    const hue = neutral ? 0 : base.h;
    const saturation = neutral ? 0 : clamp(base.s, MIN_SATURATION, MAX_SATURATION);

    // 600 is the picked colour. Every other stop keeps the stock ramp's
    // distance from 600, so a light brand stays light on the page and a dark
    // one stays dark, instead of all being forced through the indigo 59% L.
    const brandS = neutral ? 0 : base.s;
    const brandL = base.l;

    const stops = STOPS.map((_, i) => {
        if (i === BRAND_INDEX) {
            return { h: hue, s: brandS, l: brandL };
        }

        return {
            h: hue,
            s: neutral ? 0 : clamp(Math.round((saturation * STOCK_S[i]) / S_REF), 15, 100),
            l: clamp(brandL + (STOCK_L[i] - STOCK_L[BRAND_INDEX]), 0, 100),
        };
    });

    const enforceMonotonic = () => {
        for (let i = BRAND_INDEX - 1; i >= 0; i--) {
            stops[i].l = Math.min(100, Math.max(stops[i].l, stops[i + 1].l + 1));
        }

        for (let i = BRAND_INDEX + 1; i < stops.length; i++) {
            // Floor at 0, not MIN_LIGHTNESS: a near-black brand at 600 must
            // keep 700-900 at least as dark, and 12% would lighten them.
            stops[i].l = Math.max(0, Math.min(stops[i].l, stops[i - 1].l - 1));
        }
    };

    enforceMonotonic();

    // Darken the white-text stops until they clear AA, then re-establish
    // monotonicity: a clamp on 800 can otherwise leave it lighter than 900
    // or darker than 700.
    for (let i = FIRST_CLAMPED_INDEX; i < stops.length; i++) {
        while (
            stops[i].l > MIN_LIGHTNESS &&
            contrastRatio(stops[i], WHITE) < AA_NORMAL
        ) {
            stops[i].l -= 1;
        }
    }

    enforceMonotonic();

    // Measured against the brand stop — that is the fill the intake button and
    // `bg-primary` actually use. A light hex must get dark ink, not white.
    // Pure black is the fallback when the app's own ink (#0f172a-ish) misses
    // AA against a mid-red, which sits right on the 4.5 boundary.
    const BLACK = { h: 0, s: 0, l: 0 };
    const anchor = stops[BRAND_INDEX];
    let foreground =
        contrastRatio(anchor, WHITE) >= contrastRatio(anchor, FOREGROUND_DARK)
            ? WHITE
            : FOREGROUND_DARK;

    if (contrastRatio(anchor, foreground) < AA_NORMAL) {
        const blackRatio = contrastRatio(anchor, BLACK);
        if (blackRatio > contrastRatio(anchor, foreground)) foreground = BLACK;
    }

    const ramp = { foreground: triplet(foreground) };
    STOPS.forEach((stop, i) => {
        ramp[stop] = triplet(stops[i]);
    });

    return ramp;
}

/*
 * brandCssVars()
 * hex -> the CSS custom properties to set on the branded subtree, or null.
 *
 * `isDark` exists because ONE value is theme-dependent: tokens.css sets
 * `--primary: var(--primary-600)` on :root and `var(--primary-700)` under
 * .dark. Everything else in the override set is theme-neutral.
 *
 * Why the aliases are re-emitted rather than left to inherit: custom-property
 * substitution resolves at computed-value time on the element carrying the
 * DECLARATION. `--primary: var(--primary-600)` is declared on :root, so it
 * computes there against the STOCK ramp and inherits down as stock indigo no
 * matter what a descendant sets `--primary-600` to. Same for `--accent` and
 * `--color-brand`. Dropping any of the three ships a half-branded page.
 */
export function brandCssVars(hex, { isDark = false } = {}) {
    const ramp = deriveBrandRamp(hex);
    if (!ramp) return null;

    const vars = {
        '--primary-foreground': ramp.foreground,
        '--accent-foreground': ramp.foreground,

        // Aliases — see the note above. Not optional.
        '--primary': isDark ? ramp[700] : ramp[600],
        '--accent': ramp[600],
        '--color-brand': `hsl(${ramp[600]})`,

        // The public form uses `ring-ring` on every input.
        '--ring': ramp[400],
    };

    STOPS.forEach((stop) => {
        vars[`--primary-${stop}`] = ramp[stop];
    });

    // The accent ramp shifts one stop DARKER than primary at 500/600/700.
    //
    // The intake Continue / Submit buttons are `variant="accent"`
    // (`bg-accent-500`), so mapping 500 onto the brand stop (600) is what makes
    // those buttons the colour the tenant picked. Hover/active then land on
    // 700/800. Only 500/600/700 carry the button fill, so the shift is contained.
    const ACCENT_SHIFT = { 500: 600, 600: 700, 700: 800 };

    STOPS.forEach((stop) => {
        vars[`--accent-${stop}`] = ramp[ACCENT_SHIFT[stop] || stop];
    });

    return vars;
}
